import { useState } from 'react'
import { PageHeader } from '../../components/PageHeader'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { useSubscription } from '../../lib/useSubscription'
import { manageSubscription } from '../../lib/api/billing'
import { readError } from '../../lib/errors'
import type { Profile } from '../types'
import { useT } from '../../i18n'

interface Props {
  profile: Profile
  onBack: () => void
}

/**
 * Privacy screen — account closure.
 *
 * Soft-deletes via the `soft_delete_my_account` RPC, which:
 *   - Refuses while there's an active/trialing/past_due subscription,
 *     so we never end up billing an account whose login is gone.
 *   - Stamps `profiles.deleted_at` and clears the avatar photo.
 *   - Leaves submissions, votes, picks, and trophies in place so the
 *     archive + leaderboards keep working — the user's display name
 *     stays attributed to their past contributions.
 *
 * If the user is mid-subscription we don't show the destructive flow
 * at all; we surface a Manage button that opens Stripe's customer
 * portal so they can cancel first, then come back.
 */
export function PrivacyScreen({ profile: _profile, onBack }: Props) {
  const t = useT()
  const { signOut } = useAuth()
  const sub = useSubscription()
  const subBlocksDelete = sub.isActive || sub.status === 'past_due'

  const [confirmText, setConfirmText] = useState('')
  const [busy, setBusy] = useState<'delete' | 'portal' | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleDelete() {
    if (busy) return
    if (confirmText !== 'DELETE') {
      setError(t('Type DELETE in the box to confirm.'))
      return
    }
    setBusy('delete'); setError(null)
    try {
      const { error: e } = await supabase.rpc('soft_delete_my_account')
      if (e) throw e
      // Account is closed. Drop the user back to the auth screen —
      // the auth listener also catches their `deleted_at` flag on
      // future sign-in attempts and bounces them.
      await signOut()
    } catch (e) {
      setError(readError(e))
      setBusy(null)
    }
  }

  async function handleManageSubscription() {
    if (busy) return
    setBusy('portal'); setError(null)
    try {
      await manageSubscription(sub.source) // routes by where the sub lives (Stripe portal vs Apple settings)
      // On native the portal opens in the system browser and the app stays
      // in the foreground — clear the spinner. (On web we navigate away.)
      setBusy(null)
    } catch (e) {
      setError(readError(e))
      setBusy(null)
    }
  }

  return (
    <div className="screen pb-12">
      <PageHeader title={t('Privacy')} onBack={onBack} />

      <div className="px-5 py-5 space-y-6 overflow-y-auto">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {/* Account closure */}
        <section>
          <h2 className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Close account')}
          </h2>

          {subBlocksDelete ? (
            // Subscription gate — refuse the destructive flow until
            // billing is wound down. This is exactly what the RPC
            // would tell us anyway, but surfacing it up front turns
            // a confusing red error into a clear next step.
            <div className="card p-4 space-y-3 border-2 border-brand-border">
              <p className="text-sm text-brand-text font-semibold">
                {t('Cancel your subscription first.')}
              </p>
              <p className="text-xs text-brand-muted">
                {sub.source === 'stamps'
                  ? (sub.currentPeriodEnd
                      ? t('You have app time from stamps until {date}. It never renews and never charges. Look after it on your jordandraper.com account below.', { date: new Date(sub.currentPeriodEnd).toLocaleDateString() })
                      : t('You have app time from stamps. It never renews and never charges. Look after it on your jordandraper.com account below.'))
                  : (sub.currentPeriodEnd
                      ? t('You have an active All-Access subscription renewing {date}. Manage it below. Once it stops renewing, this screen will let you close the account.', { date: new Date(sub.currentPeriodEnd).toLocaleDateString() })
                      : t('You have an active All-Access subscription. Manage it below. Once it stops renewing, this screen will let you close the account.'))}
              </p>
              <button
                type="button"
                onClick={handleManageSubscription}
                disabled={busy !== null}
                className="btn-primary"
              >
                {busy === 'portal' ? 'Opening…' : t('Manage subscription')}
              </button>
            </div>
          ) : (
            <div className="card p-4 space-y-3 border-2 border-brand-danger/30 bg-brand-danger/5">
              <p className="text-sm text-brand-text">{t('Closing your account signs you out for good and removes your profile photo. Your past weekly stories, Colorful picks, and trophies stay in the archive under your chosen display name. We don’t rewrite history other people voted on.')}</p>
              <p className="text-xs text-brand-muted">
                {t('Type')} <span className="font-mono font-bold text-brand-danger">DELETE</span> {t('to confirm.')}
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value.toUpperCase().slice(0, 6))}
                placeholder="DELETE"
                className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                           text-brand-text font-mono tracking-widest text-center
                           focus:outline-hidden focus:border-brand-danger focus:ring-2
                           focus:ring-brand-danger/20 transition-all"
              />
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy !== null || confirmText !== 'DELETE'}
                className="w-full py-3 rounded-2xl bg-brand-danger text-white
                           font-semibold text-sm
                           active:scale-95 transition-transform
                           disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy === 'delete' ? 'Closing…' : t('Close my account')}
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

